import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Id } from '@contracts/common';
import type { UserPickerEntry } from '@contracts/identity';
import type { Recipient, RecipientInput } from '@contracts/tasks';
import { Combobox, type ComboOption } from '@/components/ui/combobox';
import { TEXT } from '@/components/typography';
import { api, ApiClientError } from '../../api/client';
import { PersonLabel, type Audience } from '../sharing/audience';
import { normalizeSearch } from '../workplace/model';

/**
 * Who the work is for: always a BOD member who signs in (projects spec §4.1). A partner outside the company is a
 * stakeholder of the work, never its recipient.
 */

/*
 * The field furniture the task forms and DateField share, so they read as one form: the label (the prototype
 * Label's own role), the control, then the hint or the error. (The task page's TaskForm that first used it went away
 * on 2026-09-25.)
 */
export const FIELD = 'flex min-w-0 flex-col gap-1.5';
export const FIELD_LABEL = TEXT.label;
export const FIELD_HINT = TEXT.hint;
export const FIELD_ERROR = TEXT.fieldError;

export const USERS_KEY = ['users'] as const;
const NO_USERS: UserPickerEntry[] = [];

export function useActiveUsers() {
  return useQuery<UserPickerEntry[]>({
    queryKey: USERS_KEY, queryFn: () => api<UserPickerEntry[]>('/api/users'), staleTime: 60_000,
  });
}

/**
 * The one string that names a recipient across the whole product: the picker's option value, the task
 * list's `recipient=` filter and the link a blocked deletion offers all speak it, so there is a single
 * parser to get wrong. A task is always given to an internal user (projects spec §4.1), so only `user:` parses.
 */
export type RecipientRef = { kind: 'user'; id: Id };

export function parseRecipientRef(selector: string): RecipientRef | null {
  const separator = selector.indexOf(':');
  if (separator < 0) return null;
  const kind = selector.slice(0, separator);
  const id = selector.slice(separator + 1);
  if (!id || kind !== 'user') return null;
  return { kind, id };
}

export function recipientSelector(recipient: Recipient): string {
  return `user:${recipient.userId}`;
}

/** Everything the control owns. It is a value the form holds, so a refused save changes none of it. */
export type RecipientDraft = { selector: string };

export function emptyRecipientDraft(): RecipientDraft {
  return { selector: '' };
}

export function recipientDraftOf(recipient: Recipient): RecipientDraft {
  return { selector: recipientSelector(recipient) };
}

/**
 * The draft as the API takes it. A missing choice is refused here rather than sent, because the server's
 * answer to an absent recipient says nothing a person could act on.
 */
export function recipientInputOf(draft: RecipientDraft): RecipientInput {
  const reference = parseRecipientRef(draft.selector);
  if (!reference) throw new ApiClientError(422, 'VALIDATION', 'Chọn người nhận', { recipient: 'Chọn người nhận' });
  return { kind: 'user', userId: reference.id };
}

type Named = { id: Id; name: string };

/**
 * The picker's rows (PO ruling 4): "Tôi" first, then the other BOD members, each filtered by `query` without regard
 * to case or accents ("minh" finds "Trần Văn Minh"). A value that is no longer a choice — somebody who left — stays
 * listed as the current one, and cannot be picked. An active person outside `audience` is marked "sẽ được thêm vào
 * chia sẻ" (sharing spec §8.3, L5-6): giving them the work adds them to its home.
 */
export function recipientOptions({ query, users, userId, current, currentName, audience }: {
  query: string;
  users: readonly Named[];
  userId: Id;
  current: string;
  /** How to name `current` when no list holds it (a person who left). */
  currentName?: string;
  /** Who already sees the work; `'public'` or absent marks nobody. */
  audience?: Audience;
}): ComboOption[] {
  const wanted = normalizeSearch(query);
  const fits = (name: string) => wanted === '' || normalizeSearch(name).includes(wanted);
  const row = (selector: string, name: string, group: string, id?: Id): ComboOption => ({
    key: selector, value: selector, group, current: selector === current,
    label: id === undefined ? name : <PersonLabel name={name} audience={audience} userId={id} />,
  });
  const out: ComboOption[] = [];
  const me = users.find((user) => user.id === userId);
  if (me !== undefined && fits(me.name)) out.push(row(`user:${me.id}`, me.name, 'Tôi', me.id));
  const listed = new Set(users.map((user) => `user:${user.id}`));
  if (current.includes(':') && !listed.has(current) && currentName !== undefined && fits(currentName)) {
    out.push({ ...row(current, currentName, 'BOD'), disabled: true });
  }
  for (const user of users) if (user.id !== userId && fits(user.name)) out.push(row(`user:${user.id}`, user.name, 'BOD', user.id));
  return out;
}

/**
 * The searchable list of recipients. `onPick` gets the chosen selector. The query is the picker's own: it starts
 * empty each time the picker opens.
 */
export function RecipientCombobox({
  userId, value, currentName, audience, onPick, inputRef, autoFocus, invalid, describedBy,
}: {
  userId: Id;
  value: string;
  currentName?: string;
  /** Who already sees the work: anybody else is marked as the one the save adds. */
  audience?: Audience;
  onPick: (selector: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  invalid?: boolean;
  describedBy?: string;
}) {
  // Every active account may be picked (sharing spec §8.3): one who cannot see the work yet is added to its sharing.
  const users = useActiveUsers().data ?? NO_USERS;
  const [query, setQuery] = useState('');
  const options = recipientOptions({ query, users, userId, current: value, currentName, audience });
  return (
    <Combobox
      label="Người nhận" placeholder="Tìm người nhận…" query={query} onQueryChange={setQuery}
      options={options} inputRef={inputRef} autoFocus={autoFocus} aria-invalid={invalid} aria-describedby={describedBy}
      empty="Không có ai khớp" onChoose={onPick}
    />
  );
}

/** "Việc mới"'s recipient: the searchable list of BOD members. */
export function RecipientField({ value, onChange, onPicked, error, userId, fieldRef, audience }: {
  value: RecipientDraft;
  audience?: Audience;
  onChange: (next: RecipientDraft) => void;
  /** A BOD member was chosen: nothing more to ask. */
  onPicked?: () => void;
  error?: string;
  userId: Id;
  fieldRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const id = useId();
  return (
    <div className={FIELD}>
      <RecipientCombobox
        userId={userId} value={value.selector} inputRef={fieldRef} audience={audience}
        invalid={error ? true : undefined} describedBy={error ? `${id}-error` : undefined}
        onPick={(selector) => {
          onChange({ selector });
          onPicked?.();
        }}
      />
      {error && <p className={FIELD_ERROR} id={`${id}-error`}>{error}</p>}
    </div>
  );
}
